import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppSelector } from 'src/services/redux/app/store';
import { SeenTour, TourActionContext, TourDefinition } from './types';
import { customRowToTour } from './customTours';
import TourOverlay from './TourOverlay';

/**
 * FeatureTourProvider — özellik turlarının (walkthrough) merkezi.
 * - Turlar ve eylemler kayıt olur; `startTour(id)` ile her yerden başlatılır.
 * - Görülen turlar backend'de (/feature-tours/seen) ve localStorage'da tutulur;
 *   backend yoksa/yanıt vermezse localStorage yeter.
 * - autoStart turlar ilk karşılaşmada (ve sürüm artınca) kendiliğinden açılır.
 * (7 Eki 2026 — ilk tur: bildirim paneli, src/app/components/notification-dock)
 */

const API = `${process.env.REACT_APP_API_URL}`;
const LS_KEY = 'aycrm.featureTours.seen';

interface FeatureTourContextValue {
  registerTour: (def: TourDefinition) => () => void;
  registerActions: (key: string, actions: Record<string, (...args: any[]) => any>) => () => void;
  startTour: (id: string) => boolean;
  stopTour: (reason?: 'completed' | 'skipped') => void;
  isSeen: (id: string, version?: number) => boolean;
  /** Yönetim ekranında kapatılmış tur: otomatik açılmaz, elle de başlatılmaz */
  isEnabled: (id: string) => boolean;
  /** Özel rehber listesini yeniden yükle (yönetim panelinde kayıt sonrası) */
  reloadCustomTours: () => Promise<void>;
  activeTourId: string | null;
}

interface TourSetting {
  enabled: boolean;
  auto_start: boolean;
  /** Dolu ise tur yalnız bu kullanıcılara otomatik açılır (elle başlatma herkes için serbest) */
  audience_user_ids?: number[] | null;
}

const FeatureTourContext = createContext<FeatureTourContextValue | null>(null);

const readLocal = (): Record<string, SeenTour> => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const writeLocal = (seen: Record<string, SeenTour>) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(seen));
  } catch {
    /* sessiz */
  }
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export const FeatureTourProvider: React.FC = ({ children }) => {
  const { user, isAuthorized } = useAppSelector((state) => state.auth);
  const [codeTours, setCodeTours] = useState<Record<string, TourDefinition>>({});
  const [customTours, setCustomTours] = useState<Record<string, TourDefinition>>({});
  const location = useLocation();
  const history = useHistory();
  const actionsRef = useRef<Record<string, Record<string, (...args: any[]) => any>>>({});
  const [seen, setSeen] = useState<Record<string, SeenTour>>(readLocal);
  const [seenLoaded, setSeenLoaded] = useState(false);
  const [settings, setSettings] = useState<Record<string, TourSetting>>({});
  const [active, setActive] = useState<{ id: string; step: number } | null>(null);
  const autoStartTimer = useRef<number | null>(null);
  const dismissedThisSession = useRef<Set<string>>(new Set());

  // Yönetim ayarları (/admin/feature-tours): açık/kapalı, otomatik başlasın mı
  useEffect(() => {
    if (!isAuthorized || !user?.id) return;
    let cancelled = false;
    axios
      .get(`${API}/feature-tours/settings`)
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, TourSetting> = {};
        (res?.data?.results || []).forEach((r: any) => {
          map[r.tour_id] = {
            enabled: r.enabled !== false,
            auto_start: r.auto_start !== false,
            audience_user_ids: Array.isArray(r.audience_user_ids) && r.audience_user_ids.length ? r.audience_user_ids.map(Number) : null,
          };
        });
        setSettings(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthorized, user?.id]);

  const isEnabled = useCallback((id: string) => settings[id]?.enabled !== false, [settings]);
  // Hedef kitle tanımlıysa yalnız listedeki kullanıcıya otomatik açılır
  const inAudience = useCallback(
    (id: string) => {
      const list = settings[id]?.audience_user_ids;
      return !list || !list.length || (!!user?.id && list.includes(Number(user.id)));
    },
    [settings, user?.id],
  );

  // Yönetim panelinden oluşturulan özel rehberler (bu kullanıcıya uygulananlar)
  const reloadCustomTours = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feature-tours/my`);
      const map: Record<string, TourDefinition> = {};
      (res?.data?.results || []).forEach((r: any) => {
        const def = customRowToTour(r);
        map[def.id] = def;
      });
      setCustomTours(map);
    } catch {
      /* tablo yoksa / ağ hatası: özel rehber yok */
    }
  }, []);
  useEffect(() => {
    if (!isAuthorized || !user?.id) return;
    reloadCustomTours();
  }, [isAuthorized, user?.id, reloadCustomTours]);

  // Sayfa değişince (en çok dakikada bir) listeyi tazele: yeni atanan rehberler
  // uygulama yeniden yüklenmeden görünsün
  const lastReload = useRef(0);
  useEffect(() => {
    if (!isAuthorized || !user?.id) return;
    const now = Date.now();
    if (now - lastReload.current < 60000) return;
    lastReload.current = now;
    reloadCustomTours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Kod turları + özel rehberler (aynı id olursa kod turu kazanır)
  const tours = useMemo<Record<string, TourDefinition>>(() => ({ ...customTours, ...codeTours }), [customTours, codeTours]);

  const matchesPage = useCallback((t: TourDefinition) => !t.pagePath || location.pathname === t.pagePath || location.pathname.startsWith(`${t.pagePath}/`), [location.pathname]);

  // Görülen turlar: backend + localStorage birleşimi (backend kazanır)
  useEffect(() => {
    let cancelled = false;
    if (!isAuthorized || !user?.id) {
      setSeenLoaded(true);
      return () => {};
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/feature-tours/seen`);
        const rows: Array<{ tour_id: string; version: number; status: string }> = res?.data?.results || [];
        if (cancelled) return;
        setSeen((prev) => {
          const merged = { ...prev };
          rows.forEach((r) => {
            merged[r.tour_id] = { version: Number(r.version) || 1, status: r.status === 'skipped' ? 'skipped' : 'completed' };
          });
          writeLocal(merged);
          return merged;
        });
      } catch {
        /* backend yoksa localStorage yeter */
      } finally {
        if (!cancelled) setSeenLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthorized, user?.id]);

  const isSeen = useCallback(
    (id: string, version?: number) => {
      const s = seen[id];
      if (!s) return false;
      return version === undefined ? true : s.version >= version;
    },
    [seen],
  );

  const markSeen = useCallback((id: string, version: number, status: 'completed' | 'skipped') => {
    setSeen((prev) => {
      const next = { ...prev, [id]: { version, status } };
      writeLocal(next);
      return next;
    });
    axios.post(`${API}/feature-tours/seen`, { tour_id: id, version, status }).catch(() => {});
  }, []);

  const registerTour = useCallback((def: TourDefinition) => {
    setCodeTours((prev) => ({ ...prev, [def.id]: def }));
    return () => {
      setCodeTours((prev) => {
        const next = { ...prev };
        delete next[def.id];
        return next;
      });
    };
  }, []);

  const registerActions = useCallback((key: string, actions: Record<string, (...args: any[]) => any>) => {
    actionsRef.current[key] = { ...(actionsRef.current[key] || {}), ...actions };
    return () => {
      delete actionsRef.current[key];
    };
  }, []);

  const ctx: TourActionContext = useMemo(() => ({ actions: actionsRef.current, sleep }), []);

  const startTour = useCallback(
    (id: string) => {
      if (!tours[id] || !isEnabled(id)) return false;
      if (autoStartTimer.current) {
        window.clearTimeout(autoStartTimer.current);
        autoStartTimer.current = null;
      }
      setActive({ id, step: 0 });
      return true;
    },
    [tours, isEnabled],
  );

  const stopTour = useCallback(
    (reason: 'completed' | 'skipped' = 'skipped') => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tourPreview')) {
        params.delete('tourPreview');
        const qs = params.toString();
        history.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }
      setActive((cur) => {
        if (!cur) return null;
        const def = tours[cur.id];
        if (def) {
          dismissedThisSession.current.add(cur.id);
          markSeen(cur.id, def.version, reason);
          Promise.resolve(def.onFinish?.(ctx, reason)).catch(() => {});
        }
        return null;
      });
    },
    [tours, markSeen, ctx, history],
  );

  // Otomatik başlatma: görülmemiş (veya sürümü artmış) ilk autoStart tur
  useEffect(() => {
    if (!seenLoaded || active || !isAuthorized) return;
    if (new URLSearchParams(location.search).get('tourPick') === '1') return; // öğe seçici açıkken tur başlamasın
    const candidate = Object.values(tours).find(
      (t) =>
        t.autoStart &&
        isEnabled(t.id) &&
        settings[t.id]?.auto_start !== false &&
        inAudience(t.id) &&
        matchesPage(t) &&
        !isSeen(t.id, t.version) &&
        !dismissedThisSession.current.has(t.id),
    );
    if (!candidate) return;
    if (autoStartTimer.current) window.clearTimeout(autoStartTimer.current);
    autoStartTimer.current = window.setTimeout(() => {
      autoStartTimer.current = null;
      if (document.visibilityState === 'visible') setActive({ id: candidate.id, step: 0 });
    }, candidate.autoStartDelayMs ?? 2500);
    return () => {
      if (autoStartTimer.current) {
        window.clearTimeout(autoStartTimer.current);
        autoStartTimer.current = null;
      }
    };
  }, [tours, seenLoaded, active, isAuthorized, isSeen, isEnabled, inAudience, settings, matchesPage, location.search]);

  // Önizleme: ?tourPreview=<id> ile gelen sayfada turu (görülmüş olsa da) başlat
  const previewDone = useRef<string | null>(null);
  const previewFetched = useRef<string | null>(null);
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('tourPreview');
    if (!id) {
      previewDone.current = null;
      return;
    }
    if (previewDone.current === id) return;
    if (!tours[id]) {
      // Yeni kaydedilmiş ya da bu kullanıcıya atanmamış özel rehber: doğrudan çek
      const m = /^custom-(\d+)$/.exec(id);
      if (m && previewFetched.current !== id) {
        previewFetched.current = id;
        axios
          .get(`${API}/feature-tours/custom/${m[1]}`)
          .then((res) => {
            const def = customRowToTour(res.data);
            setCustomTours((prev) => ({ ...prev, [def.id]: def }));
          })
          .catch(() => {});
      }
      return;
    }
    previewDone.current = id;
    const t = window.setTimeout(() => setActive({ id, step: 0 }), 900);
    return () => window.clearTimeout(t);
  }, [location.search, tours]);

  const value = useMemo<FeatureTourContextValue>(
    () => ({ registerTour, registerActions, startTour, stopTour, isSeen, isEnabled, reloadCustomTours, activeTourId: active?.id || null }),
    [registerTour, registerActions, startTour, stopTour, isSeen, isEnabled, reloadCustomTours, active?.id],
  );

  const activeDef = active ? tours[active.id] : null;

  return (
    <FeatureTourContext.Provider value={value}>
      {children}
      {active && activeDef && (
        <TourOverlay
          tour={activeDef}
          stepIndex={active.step}
          ctx={ctx}
          onStepChange={(step) => setActive((cur) => (cur ? { ...cur, step } : cur))}
          onFinish={() => stopTour('completed')}
          onSkip={() => stopTour('skipped')}
        />
      )}
    </FeatureTourContext.Provider>
  );
};

/** Her yerden: registerTour / registerActions / startTour */
export const useFeatureTour = (): FeatureTourContextValue => {
  const v = useContext(FeatureTourContext);
  if (!v) {
    // Sağlayıcı dışında (ör. test) sessiz çalışsın
    return {
      registerTour: () => () => {},
      registerActions: () => () => {},
      startTour: () => false,
      stopTour: () => {},
      isSeen: () => false,
      isEnabled: () => true,
      reloadCustomTours: async () => {},
      activeTourId: null,
    };
  }
  return v;
};

export default FeatureTourProvider;
